import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MessageSquare,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Pencil,
  Trash2,
  Copy,
  Star,
  Phone,
  PhoneIncoming,
  Mail,
  MessageCircle,
  Check,
  BarChart3,
  Clock,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Label,
  Switch,
  Textarea,
} from '@/components/ui'
import { promptsApi, type CreatePromptData, type UpdatePromptData } from '@/api/endpoints/prompts'
import type { Prompt, PromptFilters, PromptType } from '@/types'
import { cn, formatDate } from '@/lib/utils'

// Prompt types that match the actual backend database values
const PROMPT_TYPES: { value: string; label: string; icon: typeof Phone }[] = [
  { value: 'all', label: 'All Types', icon: MessageSquare },
  { value: 'outbound_call_script', label: 'Outbound Call Script', icon: Phone },
  { value: 'outbound_opening_line', label: 'Opening Line', icon: Phone },
  { value: 'outbound_ivr_instructions', label: 'IVR Instructions', icon: Phone },
  { value: 'inbound_prompt', label: 'Inbound Call', icon: PhoneIncoming },
  { value: 'inbound_prompt_advisor', label: 'Inbound Advisor', icon: MessageCircle },
  { value: 'voicemail_message', label: 'Voicemail', icon: Phone },
  { value: 'email_voicemail_followup', label: 'Email Follow-up', icon: Mail },
]

const getTypeIcon = (type: string) => {
  const found = PROMPT_TYPES.find(t => t.value === type)
  return found?.icon || MessageSquare
}

const getTypeColor = (type: string) => {
  if (type.startsWith('outbound_') || type.startsWith('voicemail_')) {
    return 'bg-green-100 text-green-700 border-green-200'
  }
  if (type.startsWith('inbound_')) {
    return 'bg-teal-100 text-teal-700 border-teal-200'
  }
  if (type.startsWith('email_')) {
    return 'bg-blue-100 text-blue-700 border-blue-200'
  }
  return 'bg-gray-100 text-gray-700 border-gray-200'
}

const getTypeBadgeColor = (type: string) => {
  if (type.startsWith('outbound_') || type.startsWith('voicemail_')) {
    return 'bg-green-500'
  }
  if (type.startsWith('inbound_')) {
    return 'bg-teal-500'
  }
  if (type.startsWith('email_')) {
    return 'bg-blue-500'
  }
  return 'bg-gray-500'
}

// Parse pipe-separated variations for display
const parseVariations = (content: string): string[] => {
  if (!content.includes('|')) return [content]
  return content.split('|').map(v => v.trim()).filter(Boolean)
}

export function Prompts() {
  const queryClient = useQueryClient()
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null)
  const [formData, setFormData] = useState<CreatePromptData>({
    type: 'outbound_call',
    name: '',
    content: '',
    version: '1.0',
    is_active: true,
    is_default: false,
  })

  // Build filters for API
  const apiFilters = useMemo(() => {
    const f: PromptFilters = {}
    if (typeFilter !== 'all') f.type = typeFilter as PromptType
    if (activeFilter !== 'all') f.is_active = activeFilter === 'active'
    if (searchQuery) f.search = searchQuery
    return f
  }, [typeFilter, activeFilter, searchQuery])

  // Fetch prompts
  const { data, isLoading, error } = useQuery({
    queryKey: ['prompts', apiFilters],
    queryFn: () => promptsApi.getAll(apiFilters),
  })

  const prompts = data?.data?.items || []

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreatePromptData) => promptsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
      setIsAddDialogOpen(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromptData }) => promptsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
      setIsEditDialogOpen(false)
      setSelectedPrompt(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => promptsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
      setIsDeleteDialogOpen(false)
      setSelectedPrompt(null)
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => promptsApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
    },
  })

  const resetForm = () => {
    setFormData({
      type: 'outbound_call',
      name: '',
      content: '',
      version: '1.0',
      is_active: true,
      is_default: false,
    })
  }

  const handleAddPrompt = () => {
    createMutation.mutate(formData)
  }

  const handleEditPrompt = () => {
    if (!selectedPrompt) return
    updateMutation.mutate({
      id: selectedPrompt.id,
      data: {
        name: formData.name,
        content: formData.content,
        version: formData.version,
        is_active: formData.is_active,
        is_default: formData.is_default,
        ab_group: formData.ab_group,
        ab_weight: formData.ab_weight,
      },
    })
  }

  const handleDeletePrompt = () => {
    if (!selectedPrompt) return
    deleteMutation.mutate(selectedPrompt.id)
  }

  const openEditDialog = (prompt: Prompt) => {
    setSelectedPrompt(prompt)
    setFormData({
      type: prompt.prompt_type || prompt.type || 'outbound_call',
      name: prompt.name,
      content: prompt.prompt_content || prompt.content || '',
      version: prompt.version,
      is_active: prompt.is_active,
      is_default: prompt.is_default,
      ab_group: prompt.ab_test_group || prompt.ab_group || undefined,
      ab_weight: prompt.ab_test_weight || prompt.ab_weight || undefined,
    })
    setIsEditDialogOpen(true)
  }

  const openViewDialog = (prompt: Prompt) => {
    setSelectedPrompt(prompt)
    setIsViewDialogOpen(true)
  }

  const openDeleteDialog = (prompt: Prompt) => {
    setSelectedPrompt(prompt)
    setIsDeleteDialogOpen(true)
  }

  const handleDuplicate = (prompt: Prompt) => {
    setFormData({
      type: prompt.prompt_type || prompt.type || 'outbound_call',
      name: `${prompt.name} (Copy)`,
      content: prompt.prompt_content || prompt.content || '',
      version: '1.0',
      is_active: false,
      is_default: false,
    })
    setIsAddDialogOpen(true)
  }

  const clearFilters = () => {
    setTypeFilter('all')
    setActiveFilter('all')
    setSearchQuery('')
  }

  const hasActiveFilters = typeFilter !== 'all' || activeFilter !== 'all' || searchQuery

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load prompts. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Prompt Management</h1>
          <p className="text-muted-foreground">
            Manage AI prompts for calls, emails, and qualification
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Prompt
        </Button>
      </div>

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-5">
        {PROMPT_TYPES.filter(t => t.value !== 'all').map(type => {
          const Icon = type.icon
          // Use prompt_type (backend field name) with fallback to type (legacy alias)
          const count = prompts.filter(p => (p.prompt_type || p.type) === type.value).length
          const activeCount = prompts.filter(p => (p.prompt_type || p.type) === type.value && p.is_active).length
          return (
            <Card key={type.value}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={cn('p-2 rounded-lg', getTypeColor(type.value as PromptType))}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">
                      {type.label} ({activeCount} active)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                {PROMPT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Active Filter */}
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {typeFilter !== 'all' && (
                <Badge variant="secondary">{PROMPT_TYPES.find(t => t.value === typeFilter)?.label}</Badge>
              )}
              {activeFilter !== 'all' && (
                <Badge variant="secondary">{activeFilter === 'active' ? 'Active' : 'Inactive'}</Badge>
              )}
              {searchQuery && <Badge variant="secondary">Search: {searchQuery}</Badge>}
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prompts List */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            All Prompts
            <Badge variant="outline" className="ml-2">{prompts.length} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex gap-4 animate-pulse p-4 border rounded-lg">
                  <div className="w-10 h-10 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/3" />
                    <div className="h-3 bg-muted rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : prompts.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No prompts found</h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Create your first prompt to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {prompts.map((prompt: Prompt) => {
                const promptType = prompt.prompt_type || prompt.type || 'outbound_call'
                const promptContent = prompt.prompt_content || prompt.content || ''
                const Icon = getTypeIcon(promptType)
                const variations = parseVariations(promptContent)
                const hasVariations = variations.length > 1

                return (
                  <div
                    key={prompt.id}
                    className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => openViewDialog(prompt)}
                  >
                    {/* Icon */}
                    <div className={cn('p-2 rounded-lg', getTypeColor(promptType))}>
                      <Icon className="h-5 w-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-medium">{prompt.name}</p>
                        <Badge className={cn('text-white text-xs', getTypeBadgeColor(promptType))}>
                          {promptType.replace('_', ' ')}
                        </Badge>
                        {prompt.is_default && (
                          <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            Default
                          </Badge>
                        )}
                        <Badge variant={prompt.is_active ? 'default' : 'secondary'}>
                          {prompt.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {hasVariations && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            {variations.length} variations
                          </Badge>
                        )}
                      </div>

                      {/* Content Preview */}
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {hasVariations ? variations[0] : promptContent}
                      </p>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          v{prompt.version}
                        </span>
                        <span className="flex items-center gap-1">
                          <BarChart3 className="h-3 w-3" />
                          {prompt.usage_count || 0} uses
                        </span>
                        {prompt.success_rate != null && (
                          <span className="flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            {((prompt.success_rate || 0) * 100).toFixed(0)}% success
                          </span>
                        )}
                        <span>Updated {formatDate(prompt.updated_at || prompt.created_at)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(prompt); }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicate(prompt); }}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        {!prompt.is_default && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDefaultMutation.mutate(prompt.id); }}>
                            <Star className="h-4 w-4 mr-2" />
                            Set as Default
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => { e.stopPropagation(); openDeleteDialog(prompt); }}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Prompt Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedPrompt && (
            <>
              <DialogHeader>
                {(() => {
                  const promptType = selectedPrompt.prompt_type || selectedPrompt.type || 'outbound_call'
                  return (
                    <div className={cn(
                      'flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg text-white',
                      getTypeBadgeColor(promptType)
                    )}>
                      {(() => {
                        const Icon = getTypeIcon(promptType)
                        return <Icon className="h-6 w-6" />
                      })()}
                      <div>
                        <DialogTitle className="text-white">{selectedPrompt.name}</DialogTitle>
                        <p className="text-sm opacity-90">
                          {promptType.replace('_', ' ')} prompt - v{selectedPrompt.version}
                        </p>
                      </div>
                    </div>
                  )
                })()}
              </DialogHeader>

              {(() => {
                const promptContent = selectedPrompt.prompt_content || selectedPrompt.content || ''
                const abGroup = selectedPrompt.ab_test_group || selectedPrompt.ab_group
                return (
                  <div className="space-y-4">
                    {/* Status badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {selectedPrompt.is_default && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Default
                        </Badge>
                      )}
                      <Badge variant={selectedPrompt.is_active ? 'default' : 'secondary'}>
                        {selectedPrompt.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {abGroup && (
                        <Badge variant="outline">A/B Group: {abGroup}</Badge>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-muted rounded-lg text-center">
                        <p className="text-2xl font-bold">{selectedPrompt.usage_count || 0}</p>
                        <p className="text-xs text-muted-foreground">Total Uses</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg text-center">
                        <p className="text-2xl font-bold">
                          {selectedPrompt.success_rate != null
                            ? `${((selectedPrompt.success_rate || 0) * 100).toFixed(0)}%`
                            : '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">Success Rate</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg text-center">
                        <p className="text-2xl font-bold">
                          {parseVariations(promptContent).length}
                        </p>
                        <p className="text-xs text-muted-foreground">Variations</p>
                      </div>
                    </div>

                    {/* Content */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Prompt Content</p>
                      {parseVariations(promptContent).length > 1 ? (
                        <div className="space-y-2">
                          {parseVariations(promptContent).map((variation, index) => (
                            <div key={index} className="p-3 bg-muted rounded-lg text-sm">
                              <span className="text-xs text-muted-foreground">Variation {index + 1}:</span>
                              <p className="mt-1 whitespace-pre-wrap">{variation}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 bg-muted rounded-lg text-sm whitespace-pre-wrap">
                          {promptContent}
                        </div>
                      )}
                    </div>

                    {/* Dates */}
                    <div className="flex justify-between text-xs text-muted-foreground pt-4 border-t">
                      <span>Created: {formatDate(selectedPrompt.created_at)}</span>
                      <span>Updated: {formatDate(selectedPrompt.updated_at || selectedPrompt.created_at)}</span>
                    </div>
                  </div>
                )
              })()}

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                  Close
                </Button>
                <Button onClick={() => { setIsViewDialogOpen(false); openEditDialog(selectedPrompt); }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Prompt Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-green-600 text-white">
              <Plus className="h-6 w-6" />
              <DialogTitle className="text-white">Add New Prompt</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: PromptType) => setFormData(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROMPT_TYPES.filter(t => t.value !== 'all').map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  placeholder="1.0"
                  value={formData.version}
                  onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Friendly Outbound Call"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content *</Label>
              <Textarea
                id="content"
                placeholder="Enter your prompt content. Use | to separate variations."
                value={formData.content}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={8}
              />
              <p className="text-xs text-muted-foreground">
                Tip: Use | to create variations. Example: "Hello!|Hi there!|Hey!"
              </p>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_default"
                  checked={formData.is_default}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_default: checked }))}
                />
                <Label htmlFor="is_default">Set as Default</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddPrompt}
              disabled={!formData.name || !formData.content || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Prompt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Prompt Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-blue-600 text-white">
              <Pencil className="h-6 w-6" />
              <DialogTitle className="text-white">Edit Prompt</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Input value={formData.type.replace('_', ' ')} disabled className="bg-muted capitalize" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-version">Version</Label>
                <Input
                  id="edit-version"
                  placeholder="1.0"
                  value={formData.version}
                  onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                placeholder="e.g., Friendly Outbound Call"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-content">Content *</Label>
              <Textarea
                id="edit-content"
                placeholder="Enter your prompt content"
                value={formData.content}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={8}
              />
              <p className="text-xs text-muted-foreground">
                Tip: Use | to create variations. Example: "Hello!|Hi there!|Hey!"
              </p>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="edit-is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="edit-is_active">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="edit-is_default"
                  checked={formData.is_default}
                  onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, is_default: checked }))}
                />
                <Label htmlFor="edit-is_default">Set as Default</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditPrompt}
              disabled={!formData.name || !formData.content || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Prompt Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-red-600 text-white">
              <Trash2 className="h-6 w-6" />
              <DialogTitle className="text-white">Delete Prompt</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p>Are you sure you want to delete this prompt?</p>
            {selectedPrompt && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedPrompt.name}</p>
                <p className="text-sm text-muted-foreground capitalize">
                  {(selectedPrompt.prompt_type || selectedPrompt.type || 'unknown').replace('_', ' ')} - v{selectedPrompt.version}
                </p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePrompt}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Prompt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Prompts
